import { useEffect, useMemo, useState } from "react";
import { toast } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { JOB_POLLING_INTERVAL } from "~/common/constants/asyncJob";
import { useGetAsyncJobQuery, useGetCcToolsUsersQuery } from "~/graphql/mainHooks";
import {
  useExportArenaAccessMutation,
  useGetDownloadUrlQuery,
} from "~/graphql/supergraphHooks";
import { formatFullDateAndTimeWithLocalTimezoneV2 } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";
import { type UserRow } from "./components/UsersTable";

export const useFetchUsers = () => {
  const response = useGetCcToolsUsersQuery({
    context: withClientContext("staff"),
  });
  const userRows: UserRow[] = useMemo(() => {
    return (
      response.data?.usersCcTools?.edges.map((e) => ({
        id: toDbId(e.node.id),
        name: e.node.fullName || "--",
        email: e.node.emailAddress,
        activeWorkspaces: e.node.activeWorkspacesCount ? e.node.activeWorkspacesCount : 0,
        lastAccessDate: e.node.lastAccessTimestamp
          ? formatFullDateAndTimeWithLocalTimezoneV2(e.node.lastAccessTimestamp)
          : "--",
        bulkArenaAccess: e?.node?.hasBulkArenaAccess ? "Yes" : "No",
      })) || []
    );
  }, [response.data]);
  return { response, userRows };
};

export const useDownloadAuditLog = () => {
  const [jobId, setJobId] = useState<string | null>(null);

  const [exportArenaAccess, { loading }] = useExportArenaAccessMutation({
    variables: {
      input: {
        expiredAccessDays: 30, // hardcoded for now
      },
    },
  });

  const {
    data: jobData,
    startPolling,
    stopPolling,
  } = useGetAsyncJobQuery({
    skip: !jobId,
    variables: {
      jobId: jobId ?? "",
    },
    context: withClientContext("staff"),
  });

  const { data: downloadUrlData } = useGetDownloadUrlQuery({
    skip: !jobId,
    variables: {
      asyncJobId: jobId ?? "",
    },
  });

  const downloadAuditLog = async () => {
    try {
      const response = await exportArenaAccess();
      const data = response.data?.exportArenaAccess;

      if (data?.__typename === "ExportArenaAccessResponse") {
        toast.success("Generating audit log. Your download will begin shortly");
        setJobId(data?.job.id);
      } else if (data?.__typename === "MutationErrorResponse") {
        toast.error(data.message);
      } else {
        toast.error("There was an error with the operation");
      }
    } catch {
      toast.error("There was an error with the operation");
    }
  };

  useEffect(() => {
    if (jobId) {
      startPolling(JOB_POLLING_INTERVAL);
    }
    return () => {
      stopPolling();
    };
  }, [jobId, startPolling, stopPolling]);

  useEffect(() => {
    const asyncJob = jobData?.asyncJob;
    if (asyncJob?.status === "COMPLETED") {
      stopPolling();
      if (downloadUrlData?.arenaAccessExportFileUrl?.url) {
        window.location.href = downloadUrlData?.arenaAccessExportFileUrl?.url;
        setJobId(null);
      }
    } else if (asyncJob?.status === "ERROR") {
      stopPolling();
      toast.error("There was an error generating the audit log");
      setJobId(null);
    }
  }, [jobData, stopPolling, downloadUrlData]);

  return { downloadAuditLog, loading };
};
